/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Select } from '@mantine/core';
import AppStack from '../../../../../common/AppStack';
import AppText from '../../../../../common/AppText';
import AppFlexbox from '../../../../../common/AppFlexbox';
import { REG_FORM_CONTROL_TYPE_ENUM } from '../../../../../../config/constants';
import { VARIANT_STYLE_ENUM } from './wsVariantPickerConfig';
import WsVariantColorPicker from './WsVariantColorPicker';
import { isVariantOptionUnavailable } from '../../../../../../helpers/shop/webstoreHelper';
import ProductInputDisplay from '../../sections/productInformation/ProductInputDisplay';
import { useMediaQueries } from '../../../../../../helpers/hooks';

const WsVariantPickerControls = ({
  selectedOptions,
  ecomStoreCartProductInputAnswers,
  ecomStoreProduct,
  variantOptions,
  onVariantChange,
  onInputChange,
  selectedVariant,
  submitting,
  mediaQueries,
  config,
  baseStoreUrl,
  onNavigate,
  isBundleProduct,
  isCartEdit
}) => {
  const { isTabletOrSmaller } = mediaQueries || useMediaQueries();
  const allVariantsHaveColorsSet = ecomStoreProduct?.ecomStoreProductVariants.every(
    (variant) => !!variant.ecomVendorProductVariant.primaryColor
  );
  const colorOption = variantOptions.find(
    (op) => op.name.toLowerCase() === 'color'
  );
  const showColorSwatches = allVariantsHaveColorsSet && !!colorOption;

  return (
    (variantOptions.length > 0 ||
      ecomStoreProduct.ecomVendorProduct.ecomVendorProductInputs.length >
        0) && (
      <Grid gutter={8} style={{ paddingBottom: 0 }}>
        {!isCartEdit && showColorSwatches && (
          <Grid.Col span={12} style={{ alignSelf: 'end' }}>
            <WsVariantColorPicker
              colorOption={colorOption}
              disabled={submitting}
              ecomStoreProduct={ecomStoreProduct}
              onVariantChange={onVariantChange}
              selectedOptions={selectedOptions}
              selectedVariant={selectedVariant}
            />
          </Grid.Col>
        )}

        {(!isCartEdit || isBundleProduct) &&
          variantOptions
            .filter(
              (op) =>
                op.values.length > 1 &&
                (!showColorSwatches || op.sort !== colorOption.sort)
            )
            .map((op) => (
              <Grid.Col
                key={op.name}
                span={
                  !isTabletOrSmaller &&
                  (config.variantStyle === VARIANT_STYLE_ENUM.DROPDOWN ||
                    op.values.length > 10)
                    ? 6
                    : 12
                }
                style={{ alignSelf: 'end' }}
              >
                {config.variantStyle === VARIANT_STYLE_ENUM.BUTTON_GROUP &&
                op.values.length <= (isTabletOrSmaller ? 3 : 5) ? (
                  <AppStack style={{ gap: 5 }}>
                    <AppText style={{ fontSize: 14 }}>{op.name}</AppText>
                    <Button.Group style={{ width: '100%' }}>
                      {op.values
                        .sort((a, b) => a.sort - b.sort)
                        .map((v) => (
                          <Button
                            key={v.value}
                            color="dark"
                            disabled={
                              submitting ||
                              isVariantOptionUnavailable(
                                op,
                                v.value,
                                selectedOptions,
                                ecomStoreProduct
                              )
                            }
                            onClick={() =>
                              onVariantChange(
                                ecomStoreProduct.pkEcomStoreProduct,
                                [
                                  ...selectedOptions.filter(
                                    (f) => f.name !== op.name
                                  ),
                                  {
                                    nameSort: op.sort,
                                    name: op.name,
                                    value: v.value
                                  }
                                ]
                              )
                            }
                            size="compact-md"
                            style={{
                              width: '100%',
                              overflow: 'visible',
                              whiteSpace: 'nowrap',
                              textOverflow: 'unset',
                              flexGrow: 1,
                              fontSize: 14,
                              padding: '0px 4px'
                            }}
                            variant={
                              v.value ===
                              selectedOptions.find((f) => f.name === op.name)
                                ?.value
                                ? 'filled'
                                : 'outline'
                            }
                          >
                            {v.value}
                          </Button>
                        ))}
                    </Button.Group>
                  </AppStack>
                ) : config.variantStyle === VARIANT_STYLE_ENUM.PILLS &&
                  op.values.length <= 10 ? (
                  <AppStack style={{ gap: 5 }}>
                    <AppText
                      style={{
                        fontSize: 14,
                        fontWeight: 500
                      }}
                    >
                      {op.name}
                    </AppText>
                    <AppFlexbox
                      style={{ gap: 8, flexWrap: 'wrap' }}
                      variant="outline"
                    >
                      {op.values
                        .sort((a, b) => a.sort - b.sort)
                        .map((v) => {
                          const isSelected = selectedOptions.some(
                            (f) => f.name === op.name && f.value === v.value
                          );

                          return (
                            <Button
                              key={v.value}
                              color={isSelected ? 'dark' : 'gray'}
                              disabled={
                                submitting ||
                                isVariantOptionUnavailable(
                                  op,
                                  v.value,
                                  selectedOptions,
                                  ecomStoreProduct
                                )
                              }
                              onClick={() =>
                                onVariantChange(
                                  ecomStoreProduct.pkEcomStoreProduct,
                                  [
                                    ...selectedOptions.filter(
                                      (f) => f.name !== op.name
                                    ),
                                    {
                                      nameSort: op.sort,
                                      name: op.name,
                                      value: v.value
                                    }
                                  ]
                                )
                              }
                              radius={100}
                              size="compact-md"
                              style={{ fontSize: 12, minWidth: 25 }}
                              variant={isSelected ? 'filled' : 'outline'}
                            >
                              {v.value}
                            </Button>
                          );
                        })}
                    </AppFlexbox>
                  </AppStack>
                ) : (
                  <Select
                    data={op.values
                      .sort((a, b) => a.sort - b.sort)
                      .map((v) => ({
                        label: v.value,
                        value: v.value,
                        disabled:
                          submitting ||
                          isVariantOptionUnavailable(
                            op,
                            v.value,
                            selectedOptions,
                            ecomStoreProduct
                          )
                      }))}
                    disabled={submitting}
                    label={op.name}
                    onChange={(value) => {
                      if (value) {
                        onVariantChange(ecomStoreProduct.pkEcomStoreProduct, [
                          ...selectedOptions.filter((f) => f.name !== op.name),
                          { nameSort: op.sort, name: op.name, value }
                        ]);
                      }
                    }}
                    radius={0}
                    value={
                      selectedOptions.find((f) => f.name === op.name)?.value
                    }
                  />
                )}
              </Grid.Col>
            ))}

        {ecomStoreProduct.ecomVendorProduct.ecomVendorProductInputs
          .sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label))
          .map((p) => {
            const inputAnswer = ecomStoreCartProductInputAnswers.find(
              (a) => a.fkEcomVendorProductInput === p.pkEcomVendorProductInput
            );
            return (
              <Grid.Col
                key={p.pkEcomVendorProductInput}
                span={
                  isTabletOrSmaller ||
                  p.fkRegFormControlType ===
                    REG_FORM_CONTROL_TYPE_ENUM.CHECKBOX ||
                  p.fkRegFormControlType ===
                    REG_FORM_CONTROL_TYPE_ENUM.FILE_UPLOAD
                    ? 12
                    : 6
                }
                style={{
                  alignSelf: 'end',
                  marginTop:
                    p.fkRegFormControlType ===
                    REG_FORM_CONTROL_TYPE_ENUM.CHECKBOX
                      ? 5
                      : 0
                }}
              >
                <ProductInputDisplay
                  key={p.pkEcomVendorProductInput}
                  baseStoreUrl={baseStoreUrl}
                  defaultImagePreview={
                    inputAnswer?.previewImage || inputAnswer?.value
                  }
                  disabled={inputAnswer?.isAutopopulated || submitting}
                  ecomVendorProductInput={p}
                  onChange={(value, type, previewImage) =>
                    onInputChange(ecomStoreProduct.pkEcomStoreProduct, [
                      ...ecomStoreCartProductInputAnswers.filter(
                        (a) =>
                          a.fkEcomVendorProductInput !==
                          p.pkEcomVendorProductInput
                      ),
                      {
                        fkEcomVendorProductInput: p.pkEcomVendorProductInput,
                        fkRegFormControlType: p.fkRegFormControlType,
                        previewImage,
                        file: type === 'file' ? value : null,
                        value: type === 'file' ? null : value
                      }
                    ])
                  }
                  onNavigate={onNavigate}
                  radius={0}
                  value={inputAnswer?.value}
                />
              </Grid.Col>
            );
          })}
      </Grid>
    )
  );
};

WsVariantPickerControls.propTypes = {
  baseStoreUrl: PropTypes.string,
  config: PropTypes.object,
  ecomStoreCartProductInputAnswers: PropTypes.array,
  ecomStoreProduct: PropTypes.object,
  isBundleProduct: PropTypes.bool,
  isCartEdit: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onInputChange: PropTypes.func,
  onNavigate: PropTypes.func,
  onVariantChange: PropTypes.func,
  selectedOptions: PropTypes.array,
  selectedVariant: PropTypes.object,
  submitting: PropTypes.bool,
  variantOptions: PropTypes.array
};

export default WsVariantPickerControls;
